import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Phone, Calendar, Heart, DollarSign, MapPin, Utensils, AlertCircle, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const experienceOptions = [
  'Beach', 'City', 'Nature', 'Mountains', 'Skiing', 
  'Adventure', 'Cultural', 'Kid-Friendly', 'Multi-city', 
  'Wellness', 'All Inclusive'
];

const budgetOptions = [
  { value: '$100-200', label: '$100 - $200 per night' },
  { value: '$200-400', label: '$200 - $400 per night' },
  { value: '$400-600', label: '$400 - $600 per night' },
  { value: '$600+', label: '$600+ per night' }
];

const planningOptions = [
  'Hotels', 'Flights', 'Activities/Touring', 'Restaurant Reservations'
];

const priorityOptions = [
  'Sightseeing', 'Wellness', 'Shopping', 'History', 
  'Local Cuisine', 'Nature', 'Romantic Spots', 
  'Fun with the Kids', 'Go with the Flow'
];

const TravelerProfile = () => {
  const { profile: initialProfile, fetchProfile } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    date_of_birth: '',
    experience_types: [],
    hotel_budget: '',
    planning_scope: [],
    top_priorities: [],
    past_trips_loved: '',
    favorite_hotels: '',
    dietary_requirements: '',
    allergies: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: ''
  });

  useEffect(() => {
    if (initialProfile) {
      setFormData({
        full_name: initialProfile.full_name || '',
        phone: initialProfile.phone || '',
        date_of_birth: initialProfile.date_of_birth || '',
        experience_types: initialProfile.experience_types || [],
        hotel_budget: initialProfile.hotel_budget || '',
        planning_scope: initialProfile.planning_scope || [],
        top_priorities: initialProfile.top_priorities || [],
        past_trips_loved: initialProfile.past_trips_loved || '',
        favorite_hotels: initialProfile.favorite_hotels || '',
        dietary_requirements: initialProfile.dietary_requirements || '',
        allergies: initialProfile.allergies || '',
        emergency_contact_name: initialProfile.emergency_contact_name || '',
        emergency_contact_phone: initialProfile.emergency_contact_phone || '',
        emergency_contact_relationship: initialProfile.emergency_contact_relationship || ''
      });
    }
  }, [initialProfile]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (field, value) => {
    const current = formData[field] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    
    // Limit priorities to 3
    if (field === 'top_priorities' && updated.length > 3) {
      toast.error('You can select up to 3 priorities');
      return;
    }
    
    setFormData({ ...formData, [field]: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully!');
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="traveler-profile">
      <div className="card">
        <h2 className="text-2xl font-heading font-bold text-maroon mb-2">Traveler Profile</h2>
        <p className="text-maroon/70 mb-6">
          Help us personalize your travel experiences by completing your profile.
        </p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <section>
            <h3 className="text-lg font-heading font-bold text-maroon mb-4 flex items-center gap-2">
              <User size={20} className="text-terracotta" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="input-field"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-field"
                  data-testid="profile-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="input-field"
                  data-testid="profile-dob-input"
                />
              </div>
            </div>
          </section>

          {/* Travel Preferences */}
          <section>
            <h3 className="text-lg font-heading font-bold text-maroon mb-4 flex items-center gap-2">
              <Heart size={20} className="text-terracotta" />
              Travel Preferences
            </h3>
            
            <div className="space-y-6">
              <div>
                <Label className="mb-3 block">What type of experiences do you enjoy? (Select all that apply)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {experienceOptions.map((exp) => (
                    <label 
                      key={exp}
                      className={`flex items-center gap-2 p-3 rounded-sm border-2 cursor-pointer transition-colors ${
                        formData.experience_types.includes(exp)
                          ? 'border-terracotta bg-terracotta/10'
                          : 'border-maroon/20 hover:border-maroon/40'
                      }`}
                    >
                      <Checkbox
                        checked={formData.experience_types.includes(exp)}
                        onCheckedChange={() => handleCheckboxChange('experience_types', exp)}
                      />
                      <span className="text-sm text-maroon">{exp}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-3 block flex items-center gap-2">
                  <DollarSign size={16} />
                  Hotel Budget (per night)
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {budgetOptions.map((option) => (
                    <label 
                      key={option.value}
                      className={`flex items-center justify-center p-3 rounded-sm border-2 cursor-pointer transition-colors text-center ${
                        formData.hotel_budget === option.value
                          ? 'border-terracotta bg-terracotta/10'
                          : 'border-maroon/20 hover:border-maroon/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="hotel_budget"
                        value={option.value}
                        checked={formData.hotel_budget === option.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <span className="text-sm text-maroon font-medium">{option.value}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-3 block">What would you like us to plan? (Select all that apply)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {planningOptions.map((option) => (
                    <label 
                      key={option}
                      className={`flex items-center gap-2 p-3 rounded-sm border-2 cursor-pointer transition-colors ${
                        formData.planning_scope.includes(option)
                          ? 'border-terracotta bg-terracotta/10'
                          : 'border-maroon/20 hover:border-maroon/40'
                      }`}
                    >
                      <Checkbox
                        checked={formData.planning_scope.includes(option)}
                        onCheckedChange={() => handleCheckboxChange('planning_scope', option)}
                      />
                      <span className="text-sm text-maroon">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-3 block">Top 3 Priorities for Your Trips</Label>
                <p className="text-xs text-maroon/60 mb-3">Select up to 3 priorities</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {priorityOptions.map((priority) => (
                    <label 
                      key={priority}
                      className={`flex items-center gap-2 p-3 rounded-sm border-2 cursor-pointer transition-colors ${
                        formData.top_priorities.includes(priority)
                          ? 'border-terracotta bg-terracotta/10'
                          : 'border-maroon/20 hover:border-maroon/40'
                      }`}
                    >
                      <Checkbox
                        checked={formData.top_priorities.includes(priority)}
                        onCheckedChange={() => handleCheckboxChange('top_priorities', priority)}
                      />
                      <span className="text-sm text-maroon">{priority}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Travel History */}
          <section>
            <h3 className="text-lg font-heading font-bold text-maroon mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-terracotta" />
              Travel History
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="past_trips_loved">
                  What trips have really worked for you? (Destinations, activities, what you loved most)
                </Label>
                <Textarea
                  id="past_trips_loved"
                  name="past_trips_loved"
                  value={formData.past_trips_loved}
                  onChange={handleChange}
                  rows={4}
                  className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
                  placeholder="Tell us about your favorite trips, what made them special..."
                  data-testid="profile-past-trips-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="favorite_hotels">Hotels you've loved</Label>
                <Textarea
                  id="favorite_hotels"
                  name="favorite_hotels"
                  value={formData.favorite_hotels}
                  onChange={handleChange}
                  rows={3}
                  className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
                  placeholder="Any hotels that stood out to you..."
                  data-testid="profile-hotels-input"
                />
              </div>
            </div>
          </section>

          {/* Health & Dietary */}
          <section>
            <h3 className="text-lg font-heading font-bold text-maroon mb-4 flex items-center gap-2">
              <Utensils size={20} className="text-terracotta" />
              Health & Dietary Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dietary_requirements">Dietary Requirements</Label>
                <Input
                  id="dietary_requirements"
                  name="dietary_requirements"
                  value={formData.dietary_requirements}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Vegetarian, vegan, gluten-free, etc."
                  data-testid="profile-dietary-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="allergies">Allergies</Label>
                <Input
                  id="allergies"
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Food allergies, medication allergies, etc."
                  data-testid="profile-allergies-input"
                />
              </div>
            </div>
          </section>

          {/* Emergency Contact */}
          <section>
            <h3 className="text-lg font-heading font-bold text-maroon mb-4 flex items-center gap-2">
              <AlertCircle size={20} className="text-terracotta" />
              Emergency Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_name">Contact Name</Label>
                <Input
                  id="emergency_contact_name"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleChange}
                  className="input-field"
                  data-testid="profile-emergency-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_phone">Contact Phone</Label>
                <Input
                  id="emergency_contact_phone"
                  name="emergency_contact_phone"
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={handleChange}
                  className="input-field"
                  data-testid="profile-emergency-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_relationship">Relationship</Label>
                <Input
                  id="emergency_contact_relationship"
                  name="emergency_contact_relationship"
                  value={formData.emergency_contact_relationship}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Spouse, Parent, Sibling, etc."
                  data-testid="profile-emergency-relationship-input"
                />
              </div>
            </div>
          </section>

          <Button 
            type="submit" 
            className="btn-primary w-full sm:w-auto"
            disabled={loading}
            data-testid="save-profile-btn"
          >
            <Save size={18} className="mr-2" />
            {loading ? 'Saving...' : 'Save Profile'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default TravelerProfile;
